import axios from 'axios';
import { API_URL } from '../config/api';
import SessionService from './SessionService';
import type { TemplateButtonDraft, TemplateVariableDraft } from '../utils/sparkMessageTemplateRules';

export interface SparkOfficialTemplateSummary {
  id: string;
  name: string;
  description?: string | null;
  isOfficial: boolean;
  metaTemplateStatus?: string | null;
  metaTemplateName?: string | null;
  category?: string | null;
  language?: string | null;
  metaRejectedReason?: string | null;
  linePhone?: string | null;
  entrypointId?: string | null;
  boundToSector?: boolean;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface SparkOfficialTemplateDetail extends SparkOfficialTemplateSummary {
  body?: string | null;
  footer?: string | null;
  header?: { format: string; text?: string | null; mediaId?: string | null } | null;
  buttons?: TemplateButtonDraft[];
  variables?: TemplateVariableDraft[];
  commonVariables?: Array<{ key: string; label: string; required: boolean }>;
  contentEditable?: boolean;
}

export interface SaveSparkOfficialTemplateBody {
  name: string;
  description?: string | null;
  whatsappEntrypointId?: string;
  metaTemplateName?: string;
  category?: string;
  language?: string;
  header?: { format: string; text?: string; mediaId?: string } | null;
  body: string;
  footer?: string | null;
  buttons: TemplateButtonDraft[];
  variables: TemplateVariableDraft[];
}

const authHeaders = () => {
  const token = SessionService.getToken();
  if (!token) throw new Error('Authentication token not found');
  return { Authorization: `Bearer ${token}` };
};

const base = (sectorId: number) =>
  `${API_URL}/v1/spark-message-templates?sectorId=${sectorId}`;

function mapDetail(data: SparkOfficialTemplateDetail): SparkOfficialTemplateDetail {
  return {
    ...data,
    buttons: data.buttons ?? [],
    variables: (data.variables ?? []).map((v) => ({
      index: v.index,
      title: v.title ?? '',
      example: v.example ?? '',
      kind: v.kind === 'lead' ? 'lead' : 'common',
      source: v.source ?? null,
    })),
  };
}

export async function listOfficialTemplates(sectorId: number): Promise<SparkOfficialTemplateSummary[]> {
  const response = await axios.get(base(sectorId), { headers: authHeaders() });
  return response.data?.items ?? [];
}

export async function getOfficialTemplate(sectorId: number, templateId: string): Promise<SparkOfficialTemplateDetail> {
  const response = await axios.get(
    `${API_URL}/v1/spark-message-templates/${encodeURIComponent(templateId)}?sectorId=${sectorId}`,
    { headers: authHeaders() },
  );
  return mapDetail(response.data || {});
}

export async function createOfficialTemplate(
  sectorId: number,
  body: SaveSparkOfficialTemplateBody,
): Promise<SparkOfficialTemplateDetail> {
  const response = await axios.post(base(sectorId), body, {
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
  });
  return mapDetail(response.data || {});
}

export async function updateOfficialTemplate(
  sectorId: number,
  templateId: string,
  body: SaveSparkOfficialTemplateBody,
): Promise<SparkOfficialTemplateDetail> {
  const response = await axios.patch(
    `${API_URL}/v1/spark-message-templates/${encodeURIComponent(templateId)}?sectorId=${sectorId}`,
    body,
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } },
  );
  return mapDetail(response.data || {});
}

export async function deleteOfficialTemplate(sectorId: number, templateId: string): Promise<void> {
  await axios.delete(
    `${API_URL}/v1/spark-message-templates/${encodeURIComponent(templateId)}?sectorId=${sectorId}`,
    { headers: authHeaders() },
  );
}

export async function uploadOfficialTemplateMedia(sectorId: number, file: File): Promise<string> {
  const body = new FormData();
  body.append('file', file);
  const response = await axios.post(
    `${API_URL}/v1/spark-message-templates/media?sectorId=${sectorId}`,
    body,
    { headers: authHeaders() },
  );
  const mediaId = response.data?.mediaId as string | undefined;
  if (!mediaId) throw new Error('Upload did not return a media id.');
  return mediaId;
}
